import React, {useLayoutEffect} from "react";
import {apiV2} from "api/firebase";
import {ExternalControlType} from "view/components/RichTextEditor/controls/externalControl";
import {useCurrentPublicShow, useCurrentShowId} from "hooks";
import {useSelector} from "react-redux";
import {selectUser} from "store/auth/selectors";
import {useJetStore} from "store/jet/jet.store";
import {useCurrentExhibitor} from "store/current-exhibitor/current-exhibitor.store";
import useJet from "hooks/show/jet/useJet";
import useCurrentExhibitorPairId from "hooks/useCurrentExhibitorPairId";
import useCurrentExhibitorPair from "hooks/useCurrentExhibitorPair";
import CloseSnackbarButton from "view/components/Snackbar/notistack/CloseButton";
import {UNRESOLVED_VARIABLE_HTML} from "tools/task/unresolvedVariable";
import {useSnackbar} from "notistack";
import {
  AditusType,
  AspType,
  ExhibitorUserConnectionType,
  ExpoPlatformType,
  JetType,
  Roles,
  SitkaType,
  VisitType,
} from "@evennt-tech/api-v2-core";
import {IGetPublicInfoShowRes} from "@evennt-tech/api-v2-external";
import {AxiosError} from "axios";

type Show = IGetPublicInfoShowRes;

/*
 * Matched on the path alone, never on a Show id: a description can hold the
 * placeholder, a link baked on another Show, or the legacy ASP path.
 */
const EXTERNAL_REDIRECT_MARKER = "/external-redirect";
const LEGACY_ASP_MARKER = "/redirect-to-asp";

/** The `?type=` of a stored link; the pre-`?type=` ASP path means ASP. */
const typeOfLink = (link: HTMLAnchorElement): ExternalControlType | null => {
  const href = link.getAttribute("href") ?? "";

  if (href.includes(LEGACY_ASP_MARKER)) return ExternalControlType.asp;

  const type = new URL(link.href).searchParams.get("type");

  return type && type in ExternalControlType
    ? (type as ExternalControlType)
    : null;
};

/**
 * Whether this Show can actually reach the integration. JET is not in the Show
 * payload: an organiser reads it from the JET config, an exhibitor's links are
 * settled by their own request below.
 */
const isEnabledOnShow = (
  type: ExternalControlType,
  show: Show,
  isJetEnabled: boolean
): boolean => {
  switch (type) {
    case ExternalControlType.asp:
      return show.asp?.type === AspType.Pushing;
    case ExternalControlType.expoPlatform:
      return show.expoPlatform?.type === ExpoPlatformType.Pushing;
    case ExternalControlType.visit:
      return show.visit?.type === VisitType.Pushing;
    case ExternalControlType.aditus:
      return show.aditus?.type === AditusType.Pushing;
    case ExternalControlType.sitka:
      return show.sitka?.type === SitkaType.SSO;
    case ExternalControlType.jet:
      return isJetEnabled;
    default:
      return false;
  }
};

/**
 * Drops the anchor, keeps what was inside it. The integration works on this
 * Show but this reader cannot be handed off to it.
 */
const unwrap = (link: HTMLAnchorElement) => {
  const parent = link.parentNode;
  if (!parent) return;

  while (link.firstChild) {
    parent.insertBefore(link.firstChild, link);
  }

  parent.removeChild(link);
};

/** The integration is off on this Show, so the link reads as a dead variable. */
const markUnresolved = (link: HTMLAnchorElement) => {
  const template = document.createElement("template");
  template.innerHTML = UNRESOLVED_VARIABLE_HTML;
  link.replaceWith(template.content);
};

function useRedirectToExternalLink(
  containerRef?: React.RefObject<HTMLElement | null>,
  content?: string
) {
  const showId = useCurrentShowId();
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const {role, id: exhibitorId} = useSelector(selectUser);
  const isOrganiser = role === Roles.ORGANISER;

  const exhibitorPairId = useCurrentExhibitorPairId();

  const [exhibitorPair, connection, isPairLoading] = useCurrentExhibitorPair({
    showId,
    exhibitorPairId,
  });

  const isExhibitorLoaded = useCurrentExhibitor((state) => !!state.item);

  const [show, isShowLoading] = useCurrentPublicShow();

  const [jetConfig] = useJet({showId: isOrganiser ? showId : undefined});
  const isJetLoaded = useJetStore(
    (state) => !!showId && !!state.itemsLoaded[showId]
  );
  const isJetEnabled =
    !isOrganiser ||
    (jetConfig?.type === JetType.Pushing && !jetConfig?.lastError);

  useLayoutEffect(() => {
    /*
     * Scoped to this instance's own element: a document-wide query bound every
     * mounted description to every link, so one click opened thirty tabs.
     */
    const root: ParentNode = containerRef?.current ?? document;

    // Every branch below reads `showId` from this hook, so the placeholder
    // needs nothing further to resolve.
    const externalLinks = Array.from(
      root.querySelectorAll<HTMLAnchorElement>(
        `a[href*='${EXTERNAL_REDIRECT_MARKER}'], a[href*='${LEGACY_ASP_MARKER}']`
      )
    );

    if (!externalLinks.length) {
      return undefined;
    }

    // Each link is rewritten once per content, so the judgement waits for the
    // state it is made on.
    const isWaitingForJet =
      isOrganiser &&
      !!showId &&
      !isJetLoaded &&
      externalLinks.some(
        (link) => typeOfLink(link) === ExternalControlType.jet
      );
    const isWaitingForPair =
      role === Roles.EXHIBITOR &&
      !exhibitorPair &&
      (isPairLoading || !isExhibitorLoaded);
    if ((!show && isShowLoading) || isWaitingForJet || isWaitingForPair) {
      return undefined;
    }

    // Everything an SSO hand-off needs; without it no link can be followed.
    const canFollow =
      role === Roles.EXHIBITOR &&
      !!exhibitorId &&
      !!showId &&
      !!exhibitorPair &&
      !!exhibitorPair.stand &&
      !!exhibitorPair.companyToShow &&
      !!exhibitorPair.companyToShow.companyId &&
      !!show;

    const liveLinks = externalLinks.filter((link) => {
      const type = typeOfLink(link);

      if (!type || !show) {
        unwrap(link);
        return false;
      }

      if (!isEnabledOnShow(type, show, isJetEnabled)) {
        markUnresolved(link);
        return false;
      }

      if (!canFollow) {
        unwrap(link);
        return false;
      }

      return true;
    });

    // `!show` is implied by `canFollow`; spelled out to narrow it below.
    if (!liveLinks.length || !show) {
      return undefined;
    }

    const redirect = async (event: any) => {
      event.preventDefault();

      const linkHref = event.currentTarget.href;
      const url = new URL(linkHref);
      const type = url.searchParams.get("type") as ExternalControlType;

      // JET links are pair-scoped and work for every user on the pair, so
      // contractors are not turned away for JET.
      if (
        type !== ExternalControlType.jet &&
        connection?.contractorCompanyName &&
        connection?.role !== ExhibitorUserConnectionType.PRIMARY
      ) {
        enqueueSnackbar("Integration is unavailable", {
          variant: "default",
          persist: true,
          preventDuplicate: true,
          action: (snackbarId) => (
            <CloseSnackbarButton
              onClick={() => {
                closeSnackbar(snackbarId);
              }}
            />
          ),
        });

        return;
      }

      // NOTE: Old link format should be migrated to new on migration
      const isOldAspLink = url.pathname.endsWith(LEGACY_ASP_MARKER);
      const openAspLink = async () => {
        if (show.asp?.type !== AspType.Pushing) {
          enqueueSnackbar("ASP integration is disabled", {
            variant: "default",
            persist: true,
            preventDuplicate: true,
            action: (snackbarId) => (
              <CloseSnackbarButton
                onClick={() => {
                  closeSnackbar(snackbarId);
                }}
              />
            ),
          });

          return;
        }

        const {link: aspLink} = await apiV2.asp.getSsoLink({
          showId,
        });
        if (!aspLink) {
          return;
        }
        window.open(aspLink, "_blank", "noopener,noreferrer");
      };
      if (isOldAspLink) {
        await openAspLink();
        return;
      }
      switch (type) {
        case ExternalControlType.asp: {
          await openAspLink();
          return;
        }
        case ExternalControlType.expoPlatform: {
          if (show.expoPlatform?.type !== ExpoPlatformType.Pushing) {
            enqueueSnackbar("ExpoPlatform integration is disabled", {
              variant: "default",
              persist: true,
              preventDuplicate: true,
              action: (snackbarId) => (
                <CloseSnackbarButton
                  onClick={() => {
                    closeSnackbar(snackbarId);
                  }}
                />
              ),
            });

            return;
          }

          const {
            link: expoPlatformLink,
            isSyncing: isExpoSyncing,
          } = await apiV2.expoPlatform.getSSOLink({
            exhibitorPairId,
            showId,
          });

          if (isExpoSyncing) {
            enqueueSnackbar(
              "Your company is still being synced to Expo Platform. Please try again in a few minutes.",
              {variant: "info"}
            );
            return;
          }

          if (!expoPlatformLink) {
            return;
          }

          window.open(expoPlatformLink, "_blank", "noopener,noreferrer");
          return;
        }
        case ExternalControlType.visit: {
          if (show.visit?.type !== VisitType.Pushing) {
            enqueueSnackbar("Visit integration is disabled", {
              variant: "default",
              persist: true,
              preventDuplicate: true,
              action: (snackbarId) => (
                <CloseSnackbarButton
                  onClick={() => {
                    closeSnackbar(snackbarId);
                  }}
                />
              ),
            });

            return;
          }

          const {link: linkToVisit} = await apiV2.visit.getSsoLink({
            exhibitorPairId,
            showId,
          });
          if (!linkToVisit) {
            return;
          }

          window.open(linkToVisit, "_blank", "noopener,noreferrer");
          return;
        }
        case ExternalControlType.aditus: {
          if (show.aditus?.type !== AditusType.Pushing) {
            enqueueSnackbar("Aditus integration is disabled", {
              variant: "default",
              persist: true,
              preventDuplicate: true,
              action: (snackbarId) => (
                <CloseSnackbarButton
                  onClick={() => {
                    closeSnackbar(snackbarId);
                  }}
                />
              ),
            });

            return;
          }

          const {link: aditusLink} = await apiV2.aditus.getSsoLink({
            showId,
            exhibitorPairId,
          });
          if (!aditusLink) {
            return;
          }

          window.open(aditusLink, "_blank", "noopener,noreferrer");
          return;
        }
        case ExternalControlType.sitka: {
          if (show.sitka?.type !== SitkaType.SSO) {
            enqueueSnackbar("Sitka integration is disabled", {
              variant: "default",
              persist: true,
              preventDuplicate: true,
              action: (snackbarId) => (
                <CloseSnackbarButton
                  onClick={() => {
                    closeSnackbar(snackbarId);
                  }}
                />
              ),
            });

            return;
          }

          const {link: sitkaLink} = await apiV2.sitka.getSsoLink({
            showId,
            exhibitorPairId,
          });
          if (!sitkaLink) {
            return;
          }

          window.open(sitkaLink, "_blank", "noopener,noreferrer");
          return;
        }
        case ExternalControlType.jet: {
          // getSsoLink is the status check: it 404s when the integration is
          // disabled and returns `{link: null, isSyncing: true}` while the pair
          // has not been pushed yet.
          const {link: jetLink, isSyncing} = await apiV2.jet.getSsoLink({
            showId,
            exhibitorPairId,
          });

          if (!jetLink) {
            enqueueSnackbar(
              isSyncing
                ? "This JET link is still being prepared. Please try again shortly."
                : "JET integration is disabled",
              {
                variant: "default",
                persist: true,
                preventDuplicate: true,
                action: (snackbarId) => (
                  <CloseSnackbarButton
                    onClick={() => {
                      closeSnackbar(snackbarId);
                    }}
                  />
                ),
              }
            );

            return;
          }

          window.open(jetLink, "_blank", "noopener,noreferrer");
          return;
        }
        default: {
          return null;
        }
      }
    };

    liveLinks.forEach((link) => {
      link.addEventListener("click", redirect);
    });

    /*
     * An exhibitor's JET is settled here because its state is not in the Show
     * payload: `getSsoLink` doubles as the status check, and only its 404
     * means the integration is disabled. A pair still being pushed, or a
     * check that failed for another reason, keeps its wording.
     */
    const jetLinks = liveLinks.filter(
      (link) => typeOfLink(link) === ExternalControlType.jet
    );

    if (jetLinks.length) {
      apiV2.jet
        .getSsoLink({showId, exhibitorPairId})
        .then(
          ({link}) => (link ? "live" : "unavailable"),
          (e: AxiosError) =>
            e?.response?.status === 404 ? "disabled" : "unavailable"
        )
        .then((status) => {
          if (status === "live") {
            return;
          }

          jetLinks.forEach((jetLink) => {
            jetLink.removeEventListener("click", redirect);
            if (status === "disabled") {
              markUnresolved(jetLink);
            } else {
              unwrap(jetLink);
            }
          });
        });
    }

    return () => {
      liveLinks.forEach((link) => {
        link.removeEventListener("click", redirect);
      });
    };
  }, [
    role,
    show,
    isShowLoading,
    exhibitorPair,
    isPairLoading,
    isExhibitorLoaded,
    content,
    isJetLoaded,
    isJetEnabled,
  ]);
}

export default useRedirectToExternalLink;
